import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from "cn";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import type * as React from "react";
import { MENU_LABEL_TEXT, MENU_PANEL, MENU_ROW, MENU_RULE } from "./menu";

// The v3.2 select (style.css select; the Settings and sign-up pickers) on
// Radix Select: a sunken field with a chevron, and the menu panel for the
// list. Same look as apps/web's shared select under the v3.2 flag.

/** The closed select: a 44px sunken field. */
export const SELECT_TRIGGER =
  "rounded-[10px] border-0 bg-v3-surface-2 font-v3 text-[14px] font-medium text-v3-text shadow-v3-hairline hover:bg-v3-surface-3 focus:ring-2 focus:ring-v3-brand-ring";

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export function SelectTrigger({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<
  typeof SelectPrimitive.Trigger
>): React.JSX.Element {
  return (
    <SelectPrimitive.Trigger
      className={cn(
        "flex h-11 w-full items-center justify-between whitespace-nowrap px-3 py-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        SELECT_TRIGGER,
        className,
      )}
      data-slot="select-trigger"
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentPropsWithRef<
  typeof SelectPrimitive.Content
>): React.JSX.Element {
  return (
    <SelectPrimitive.Content
      position={position}
      {...props}
      className={cn(
        "relative z-50 max-h-96 min-w-32 overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        MENU_PANEL,
        className,
      )}
      data-slot="select-content"
    >
      <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
        <ChevronUp className="h-4 w-4" />
      </SelectPrimitive.ScrollUpButton>
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
        <ChevronDown className="h-4 w-4" />
      </SelectPrimitive.ScrollDownButton>
    </SelectPrimitive.Content>
  );
}

export function SelectLabel({
  className,
  ...props
}: React.ComponentPropsWithRef<
  typeof SelectPrimitive.Label
>): React.JSX.Element {
  return (
    <SelectPrimitive.Label
      className={cn("px-2 py-1.5", MENU_LABEL_TEXT, className)}
      {...props}
    />
  );
}

/** An option: a tick on the right marks the chosen one. */
export function SelectItem({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<
  typeof SelectPrimitive.Item
>): React.JSX.Element {
  return (
    <SelectPrimitive.Item
      className={cn(
        "relative flex w-full cursor-default select-none items-center py-1.5 pl-2 pr-8 outline-hidden data-disabled:pointer-events-none data-disabled:opacity-50",
        MENU_ROW,
        className,
      )}
      data-slot="select-item"
      {...props}
    >
      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export function SelectSeparator({
  className,
  ...props
}: React.ComponentPropsWithRef<
  typeof SelectPrimitive.Separator
>): React.JSX.Element {
  return (
    <SelectPrimitive.Separator
      className={cn("-mx-1 my-1 h-px", MENU_RULE, className)}
      {...props}
    />
  );
}
